/**
 * DEVICE DETECTION HOOK
 *
 * React hook for device and browser detection with reactive updates.
 *
 * FEATURES:
 * - Reactive device type detection (mobile, tablet, desktop)
 * - Browser type detection
 * - Screen size and breakpoint tracking
 * - Orientation tracking
 * - Touch capability detection
 * - OS detection
 * - Automatic updates on window resize
 * - Debounced resize handling for performance
 *
 * USAGE:
 * ```jsx
 * import { useDeviceDetection } from '../hooks/useDeviceDetection';
 *
 * function MyComponent() {
 *   const device = useDeviceDetection();
 *
 *   if (device.isMobile) {
 *     return <MobileView />;
 *   }
 *
 *   return <DesktopView />;
 * }
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getDeviceInfo,
  getDeviceType,
  getBrowserType,
  getOSType,
  getWindowSize,
  getCurrentBreakpoint,
  getOrientation,
  isMobile,
  isTablet,
  isDesktop,
  isTouchDevice,
  isIPad,
  isIPhone,
  isAndroid
} from '../utils/deviceDetection';

/**
 * Custom hook for device detection with reactive updates
 * @param {Object} options - Configuration options
 * @param {number} options.debounceDelay - Delay in ms for resize debouncing (default: 150)
 * @param {boolean} options.enableLogging - Enable console logging for debugging (default: false)
 * @returns {Object} Device information that updates on window resize
 */
export const useDeviceDetection = (options = {}) => {
  const {
    debounceDelay = 150,
    enableLogging = false
  } = options;

  // State for device information
  const [deviceInfo, setDeviceInfo] = useState(() => getDeviceInfo());

  // State for loading (useful for SSR)
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Update device information
   */
  const updateDeviceInfo = useCallback(() => {
    const newInfo = getDeviceInfo();
    setDeviceInfo(newInfo);

    if (enableLogging) {
      console.log('📱 Device info updated:', newInfo);
    }
  }, [enableLogging]);

  /**
   * Debounced resize handler
   */
  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      // Clear existing timeout
      clearTimeout(timeoutId);

      // Set new timeout
      timeoutId = setTimeout(() => {
        updateDeviceInfo();
      }, debounceDelay);
    };

    // Initial update
    updateDeviceInfo();
    setIsLoading(false);

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [updateDeviceInfo, debounceDelay]);

  /**
   * Listen for orientation changes
   */
  useEffect(() => {
    const handleOrientationChange = () => {
      // Small delay to ensure dimensions are updated
      setTimeout(() => {
        updateDeviceInfo();
      }, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [updateDeviceInfo]);

  return {
    ...deviceInfo,
    isLoading,
    refresh: updateDeviceInfo
  };
};

/**
 * Hook specifically for tracking screen size
 * @param {number} debounceDelay - Delay in ms for resize debouncing
 * @returns {Object} Screen size information
 */
export const useScreenSize = (debounceDelay = 150) => {
  const [screenSize, setScreenSize] = useState(() => getWindowSize());
  const [breakpoint, setBreakpoint] = useState(() => getCurrentBreakpoint());

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setScreenSize(getWindowSize());
        setBreakpoint(getCurrentBreakpoint());
      }, debounceDelay);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [debounceDelay]);

  return {
    width: screenSize.width,
    height: screenSize.height,
    breakpoint
  };
};

/**
 * Hook for tracking orientation changes
 * @returns {Object} Orientation information
 */
export const useOrientation = () => {
  const [orientation, setOrientation] = useState(() => getOrientation());

  useEffect(() => {
    const handleOrientationChange = () => {
      setTimeout(() => {
        setOrientation(getOrientation());
      }, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  return {
    orientation,
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape'
  };
};

/**
 * Simple hook that returns true if device is mobile
 * @returns {boolean}
 */
export const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => isMobile());

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setMobile(isMobile());
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return mobile;
};

/**
 * Simple hook that returns true if device is tablet
 * @returns {boolean}
 */
export const useIsTablet = () => {
  const [tablet, setTablet] = useState(() => isTablet());

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setTablet(isTablet());
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return tablet;
};

/**
 * Simple hook that returns true if device is desktop
 * @returns {boolean}
 */
export const useIsDesktop = () => {
  const [desktop, setDesktop] = useState(() => isDesktop());

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setDesktop(isDesktop());
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return desktop;
};

/**
 * Hook that returns device type string
 * @returns {'mobile' | 'tablet' | 'ipad' | 'desktop'}
 */
export const useDeviceType = () => {
  const [deviceType, setDeviceType] = useState(() => getDeviceType());

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setDeviceType(getDeviceType());
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return deviceType;
};

/**
 * Hook for media query matching (similar to CSS media queries)
 * @param {string} query - Media query string (e.g., '(min-width: 768px)')
 * @returns {boolean}
 */
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);

    const handleChange = (event) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [query]);

  return matches;
};

/**
 * Export static detection functions for one-off checks
 */
export {
  // From deviceDetection.js
  isMobile,
  isTablet,
  isDesktop,
  isTouchDevice,
  isIPad,
  isIPhone,
  isAndroid,
  getDeviceType,
  getBrowserType,
  getOSType,
  getWindowSize,
  getCurrentBreakpoint,
  getOrientation,
  getDeviceInfo
};

export default useDeviceDetection;
